import { Tracker, Url } from 'finhelper';
import { NotifyContainer, NotifyManager } from 'notify-component';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import FormOnForbidden from '#app/components/auth/FormOnForbidden';
import { FunnelBackgroundWarmup } from '#app/components/funnel/shared/ui/FunnelBackgroundWarmup';
import MainPage from '#app/components/mainPage/MainPage';
import { UserContextProvider } from '#app/contexts/UserContext';
import { useUserContext } from '#app/contexts/useUserContext';
import ErrorBoundary from '#app/interface/error/ErrorBoundary';
import NotFound from '#app/interface/error/NotFound/NotFound';
import HeaderLanding from '#app/interface/landing/header/Header';
import Quiz from '#app/pages/Quiz/Quiz';
import { getServer, getUser, handleAuth, logout } from '#app/store/actions/auth';
import { getContacts, handleTitle } from '#app/store/actions/interface';

import CabinetRoutes from './CabinetRoutes';
import Layout from './Layout/Layout';

interface PagesRouterStateProps {
  auth?: boolean;
  data?: any;
  coursesLoading?: boolean;
}

interface GetUserOptionsInterface {
  callback?: () => void;
  fallback?: () => void;
  force?: boolean;
  load?: boolean;
}

type PagesRouterDispatchProps = {
  getUser: (opts?: GetUserOptionsInterface) => void;
  getServer: () => void;
  handleAuth?: (payload?: any) => void;
  logout: (...args: any[]) => void;
  handleTitle?: (title: string) => void;
  getContacts: () => void;
}

type PagesRouterProps = PagesRouterStateProps & PagesRouterDispatchProps;

const EMPTY_CONFIGS: { id: number; key: string; value: string }[] = [];
const EMPTY_CONTACTS: { id: number; first_name: '' }[] = [];

const PagesRouter: React.FC<PagesRouterProps> = (props) => {
  const { auth, logout, data } = props;
  const dispatch = useDispatch();
  const [isActive, setActive] = useState<boolean | undefined>(undefined);
  const navigate = useNavigate();
  const userInfo = useMemo(
    () => ({
      data: {
        id: String(data?.id ?? ''),
        email: data?.email ?? '',
        first_name: data?.first_name ?? '',
        configs: data?.configs ?? EMPTY_CONFIGS,
        contacts: data?.contacts ?? EMPTY_CONTACTS,
      },
    }),
    [
      data?.configs,
      data?.contacts,
      data?.email,
      data?.first_name,
      data?.id,
    ]
  );
  const user = useUserContext(userInfo);

  const refreshAuthSession = useCallback((options?: { force?: boolean }) => {
    const payload: GetUserOptionsInterface = {
      force: options?.force,
      callback: () => setActive(true),
      fallback: () => setActive(true),
    };
    dispatch((getUser as (opts: GetUserOptionsInterface) => ReturnType<typeof getUser>)(payload));
  }, [dispatch]);

  useLayoutEffect(() => {
    Tracker.init();
    dispatch(getServer());
    refreshAuthSession();
  }, [dispatch, refreshAuthSession]);

  useEffect(() => {
    const handleOnline = () => {
      if (localStorage.getItem('api_token')) {
        refreshAuthSession({ force: true });
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && localStorage.getItem('api_token')) {
        refreshAuthSession({ force: true });
      }
    };

    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshAuthSession]);

  useEffect(() => {
    if (
      auth &&
      !Url.getCurrentUrl().includes('cabinet') &&
      !Url.getCurrentUrl().includes('quiz')
    ) {
      navigate('cabinet');
    }
  }, [auth, navigate]);

  if (typeof isActive === 'undefined') {
    return null;
  }

  return (
    <ErrorBoundary>
      <UserContextProvider value={user}>
        {auth ? <FunnelBackgroundWarmup /> : null}
        <Routes>
          {auth ? (
            <Route
              path="/cabinet/*"
              element={<CabinetRoutes {...props} logout={logout} />}
            />
          ) : (
            <Route path="/cabinet" element={<Navigate to="/" />} />
          )}
          <Route
            element={
              <>
                <HeaderLanding />
                <Layout />
              </>
            }
          >
            <Route path="/agreement" element={<MainPage {...props} />} />
            <Route path="/policy" element={<MainPage {...props} />} />
            <Route path="/" element={<MainPage {...props} />} />
            <Route path="/login" element={<FormOnForbidden {...props} />} />
            <Route path="/quiz" element={<Quiz />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
        <NotifyContainer>
          <NotifyManager />
        </NotifyContainer>
      </UserContextProvider>
    </ErrorBoundary>
  );
};

const enhance = connect(
  (state: {
    auth: { auth?: boolean; data?: any };
    interfaceComponents: { coursesLoading?: boolean };
  }) => ({
    auth: state.auth.auth,
    data: state.auth.data,
    coursesLoading: state.interfaceComponents.coursesLoading,
  }),
  { getUser, getServer, handleAuth, logout, handleTitle, getContacts }
);

export default enhance(PagesRouter);
